import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { PRODUCT_PLATFORM } from "@/data/content";

const MAX_POWER = 250;

export const ProductPlatform = () => {
  const [activeId, setActiveId] = useState(PRODUCT_PLATFORM[0].id);
  const active = PRODUCT_PLATFORM.find((product) => product.id === activeId);
  const powerPercentage = Math.min(100, (active.powerNum / MAX_POWER) * 100);

  return (
    <section id="specs" data-testid="product-platform-section" className="bg-baseLight px-6 py-[30px] sm:py-[40px] lg:px-12 lg:pt-[60px] lg:pb-[70px]">
      <div className="lg:mx-auto lg:max-w-[1280px]">
        <div className="max-w-2xl lg:mx-auto lg:text-center">
          <span data-testid="product-platform-eyebrow" className="text-sm font-medium text-teal lg:hidden">MODULAR PLATFORM</span>
          <h2 className="mt-3 font-heading text-[30px] font-semibold leading-tight text-ink sm:text-[35px] lg:text-[41px]">
            A modular platform built to scale
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-inkSoft">
            From small remote facilities to large agricultural microgrids, the platform scales
            power and storage capacity around the operational requirements of each site.
          </p>
        </div>

        <div data-testid="product-platform-toggle" className="mt-8 mb-[5px] flex w-fit flex-wrap gap-1.5 rounded-full border border-borderLight bg-white p-1.5 lg:mb-[50px] lg:mx-auto lg:justify-center">
          {PRODUCT_PLATFORM.map((product) => (
            <button
              key={product.id}
              type="button"
              aria-pressed={activeId === product.id}
              data-testid={`product-option-${product.id}`}
              onClick={() => setActiveId(product.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${activeId === product.id ? "bg-teal text-white" : "text-inkMuted hover:text-ink"}`}
            >
              {product.name}
            </button>
          ))}
        </div>

        <div className="overflow-visible border-borderSubtle bg-transparent sm:overflow-hidden sm:border-y-2 sm:bg-white lg:overflow-hidden lg:rounded-3xl lg:border-x lg:border-y" data-testid="product-configurator-panel">
          <div data-testid={`product-card-${active.id}`} className="grid grid-cols-1 lg:grid-cols-12">
            <div className="flex flex-col justify-between border-borderLight bg-baseLight p-7 sm:border-b sm:p-9 lg:col-span-4 lg:border-b-0 lg:border-r lg:p-10">
              <div>
                <span className="font-mono text-xs text-teal">PLATFORM / {active.name}</span>
                <h3 data-testid="product-active-name" className="mt-5 font-heading text-3xl font-semibold text-ink sm:text-4xl">
                  {active.name}
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-inkSoft">Modular hybrid energy system</p>
              </div>
              <div className="mt-12 border-t border-borderSubtle pt-6">
                <span className="text-xs text-inkMuted">Typical application</span>
                <p data-testid="product-application" className="mt-2 font-heading text-xl font-semibold leading-snug text-ink">
                  {active.application}
                </p>
              </div>
            </div>

            <div data-testid="product-specifications-panel" className="rounded-[19px] bg-white p-7 sm:rounded-none sm:p-9 lg:col-span-8 lg:p-10">
              <div className="hidden grid-cols-[150px_minmax(0,1fr)] gap-8 border-b border-borderSubtle pb-4 sm:grid">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkMuted">Specification</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkMuted">Value</span>
              </div>

              <div data-testid="product-metric-power" className="grid grid-cols-1 gap-3 border-b border-borderLight py-5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-center sm:gap-8">
                <span className="font-mono text-[11px] text-inkMuted">01 / Power</span>
                <div>
                  <span className="font-mono text-sm font-medium text-ink sm:text-base">{active.power}</span>
                  <div className="mt-3 h-px overflow-hidden bg-sage">
                    <motion.div
                      key={`${active.id}-power`}
                      initial={{ width: 0 }}
                      animate={{ width: `${powerPercentage}%` }}
                      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full bg-teal"
                    />
                  </div>
                </div>
              </div>

              <div data-testid="product-metric-storage" className="grid grid-cols-1 gap-3 border-b-0 py-5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-center sm:gap-8">
                <span className="font-mono text-[11px] text-inkMuted">02 / Typical Storage</span>
                <span className="font-mono text-sm font-medium text-ink sm:text-base">{active.storage}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div data-testid="product-platform-disclaimer" className="flex max-w-2xl items-start gap-3 text-sm text-inkMuted">
            <Info size={16} className="mt-0.5 shrink-0 text-teal" />
            <p>
              Final system sizing, storage duration and configuration depend on site load,
              <br className="hidden lg:block" /> renewable resources, engineering assessment and project requirements.
            </p>
          </div>
          <Link
            to="/products"
            data-testid="product-platform-cta"
            className="group flex shrink-0 items-center gap-1.5 text-sm font-medium text-teal hover:text-tealHover transition-colors duration-300"
          >
            Explore the Product Platform
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};
